/* eslint-disable max-classes-per-file */

import * as THREE from 'three'

import type Renderer from 'Renderer'

import FurnitureLoader, { ProgressCallback, OnUpdateCallback } from 'Renderer/FurnitureLoader'
import { Metrics } from 'Renderer/FurnitureItem'

class PendingDownload {
    promise: Promise<THREE.Object3D>

    progressCallbacks: Array<ProgressCallback>

    onUpdateCallbacks: Array<OnUpdateCallback>

    constructor(furnitureLoader: FurnitureLoader, url: string, onProgress: ProgressCallback, onUpdate: OnUpdateCallback,
        renderer: Renderer, metrics: Metrics) {
        this.progressCallbacks = [onProgress]
        this.onUpdateCallbacks = [onUpdate]

        const rootOnProgress = (event: ProgressEvent) => this.progressCallbacks.forEach(cb => cb && cb(event))
        const rootOnUpdate = () => this.onUpdateCallbacks.forEach(cb => cb && cb())

        this.promise = furnitureLoader.loadFurniture(url, rootOnProgress, rootOnUpdate, renderer, metrics)
    }
}
class FurnitureManager {
    private furnitureLoader: FurnitureLoader

    private glbs: Map<string, THREE.Object3D>

    private pendingDownloads: Map<string, PendingDownload>

    private referenceCounts:  Map<string, number>

    constructor() {
        this.furnitureLoader = new FurnitureLoader()
        this.glbs = new Map()
        this.pendingDownloads = new Map()
        this.referenceCounts = new Map()
    }

    getGlb(url: string, onProgress: ProgressCallback, onUpdate: OnUpdateCallback,
        renderer: Renderer, metrics: Metrics) : Promise<THREE.Object3D> {
        const count = this.referenceCounts.get(url)
        if (count === undefined) {
            this.referenceCounts.set(url, 1)
        } else {
            this.referenceCounts.set(url, count + 1)
        }

        const retFurnitureItem = this.glbs.get(url)
        if (retFurnitureItem) {
            const cloned = FurnitureManager.clone(retFurnitureItem)
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            cloned.glbUrl = retFurnitureItem.glbUrl
            const disassembled = FurnitureManager.disassembleGLb(cloned)
            return Promise.resolve(disassembled[0])
        }

        let pendingDownload = this.pendingDownloads.get(url)
        if (pendingDownload) {
            pendingDownload.progressCallbacks.push(onProgress)
            pendingDownload.onUpdateCallbacks.push(onUpdate)
            return pendingDownload.promise.then((furniture) => {
                const cloned = FurnitureManager.clone(furniture)
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                cloned.glbUrl = furniture.glbUrl
                const disassembled = FurnitureManager.disassembleGLb(cloned)
                return disassembled[0]
            })
        }

        pendingDownload = new PendingDownload(this.furnitureLoader, url, onProgress, onUpdate,
            renderer, metrics)
        this.pendingDownloads.set(url, pendingDownload)
        pendingDownload.promise = pendingDownload.promise.then((furniture) => {
            this.pendingDownloads.delete(url)
            this.glbs.set(url, furniture)
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line no-param-reassign
            furniture.glbUrl = url

            // The `furniture` object is a THREE.Group containing whatever was
            // loaded from the GLTF file. Sugarcube's application code will add
            // new things to this THREE.Group later on, like stackable regions
            // and bbox lines. The FurnitureManager#clone() method in this file
            // will recursively clones this THREE.Group when Sugarcube requests
            // a duplicate furniture item. However, FurnitureManager#clone()
            // should not clone things like stackables or bbox lines. Each
            // cloned THREE.Group needs to have to have its own stackable
            // regions, with different plane IDs and unique properties. The only
            // objects clone() should duplicate are objects that were originally
            // present in the GLTF file. To coordinate proper cloning we mark
            // all the objects here with `obj.geomagicalShouldClone = true`.
            // Things like stackable regions will not have this property, and so
            // will not be cloned.

            furniture.traverse(obj => {
                obj.userData.gltfDataShouldClone = true // eslint-disable-line no-param-reassign
            })

            return furniture
        })

        return pendingDownload.promise
    }

    releaseGlb(renderObject: THREE.Object3D) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const url = renderObject.glbUrl
        if (!url) {
            FurnitureManager.disposeResources(renderObject)
            return
        }

        // YP: about the filtering below: before, the combinable children weren't deleted 
        // in the redux code when the parent was deleted, and children glbs were released here.
        // Fixing the deletion in the redux state management did lead to double release,
        // hence we need to filter out the children now.
        // https://geomagical.atlassian.net/browse/GFX-661
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const glbs = FurnitureManager.disassembleGLb(renderObject).filter(glb => glb.glbUrl === url)

        glbs.forEach((glb) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const childUrl = glb.glbUrl
            const count = this.referenceCounts.get(childUrl)
            if (count === undefined || count === 1) {
                this.referenceCounts.delete(childUrl)
                this.glbs.delete(childUrl)
                FurnitureManager.disposeResources(renderObject)
            } else {
                this.referenceCounts.set(childUrl, count - 1)
            }
        })
    }

    static clone(object: THREE.Object3D): THREE.Object3D {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const ret = new object.constructor()
        ret.copy(object, false)
        /* eslint-disable @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        ret.glbUrl = object.glbUrl
        // @ts-ignore
        ret.isFurniture = object.isFurniture
        /* eslint-enable @typescript-eslint/ban-ts-comment */

        for (let i = 0; i < object.children.length; i += 1) {
            const child = object.children[i]

            if( child.userData.gltfDataShouldClone )
                ret.add(FurnitureManager.clone(child))
        }

        return ret
    }

    static disassembleGLb(rootObject: THREE.Object3D): THREE.Object3D[] {
        const childrenGlbs: THREE.Object3D[] = []
        rootObject.traverse((obj: THREE.Object3D<THREE.Event>) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (obj.glbUrl) {
                childrenGlbs.push(obj)
            }
        })
        childrenGlbs.forEach((obj: THREE.Object3D) => {
            rootObject.remove(obj)
        })
        return childrenGlbs
    }

    static disposeResources(renderObject: THREE.Object3D) {
        if (!renderObject) {
            return
        }
        renderObject.traverse((obj: THREE.Object3D<THREE.Event>) => {
            const mesh = obj as THREE.Mesh
            const material = mesh.material as THREE.MeshStandardMaterial
            if (material) {
                FurnitureManager.disposeMap(material.normalMap)
                FurnitureManager.disposeMap(material.map)
                FurnitureManager.disposeMap(material.aoMap)
                FurnitureManager.disposeMap(material.metalnessMap)
                FurnitureManager.disposeMap(material.roughnessMap)
                material.dispose()
            }
            if (mesh.geometry) {
                mesh.geometry.dispose()
            }
        })
    }

    static disposeMap(map: THREE.Texture | null) {
        if (!map) {
            return
        }
        map.dispose()
        if (map.image && map.image.close) {
            map.image.close()
        }
    }

    dispose() {
        if (this.furnitureLoader) {
            this.furnitureLoader.dispose()
        }
    }
}
 export default FurnitureManager
